/** compat_api_record.record_id 列上限 */
const MAX_RECORD_ID_LEN = 64;

/** 写入中尚未可查的 id：跳过 getById，避免工作流落盘窗口期刷 404 */
const pendingLookupIds = new Set();
/** 近期 404 的 id：短时不再打服务端，压掉并发重试噪音 */
const lookupMissUntil = new Map();
const LOOKUP_MISS_TTL_MS = 20000;
const LOOKUP_MISS_MAX = 300;

/** 图片空间本地上传临时 id（未入库），勿对 /admin/image-space 发起查询 */
export function isImageSpaceTempLocalId(id) {
  return String(id || '').startsWith('LIB-');
}

/**
 * 客户端本地生成的图片 id（未入库图片空间），对 /admin/image-space 查询必然 404。
 * 覆盖：LIB-（本地上传）、src-img-（找图选品主图占位）、composite-（POD 合成占位）、crop-（裁剪临时图）。
 * 一律用前缀判断：仓库里 crop id 存在 `crop-0` / `crop-listing-xxx` / `crop-<ts>-<rand>` 多种形态。
 * 仅用于「是否该打服务端查询」的判定，勿与 imageSpaceIdbBackup 的临时 id 语义混用。
 */
export function isImageSpaceClientLocalId(id) {
  const s = String(id || '').trim();
  if (!s) {
    return true;
  }
  return isImageSpaceTempLocalId(s)
    || s.startsWith('src-img-')
    || s.startsWith('composite-')
    || s.startsWith('crop-');
}

export function markImageSpaceLookupPending(id) {
  const s = String(id || '').trim();
  if (s) pendingLookupIds.add(s);
}

export function clearImageSpaceLookupPending(id) {
  const s = String(id || '').trim();
  if (!s) return;
  pendingLookupIds.delete(s);
  lookupMissUntil.delete(s);
}

function pruneLookupMissCache(now = Date.now()) {
  if (lookupMissUntil.size <= LOOKUP_MISS_MAX) return;
  for (const [key, until] of lookupMissUntil) {
    if (until <= now) lookupMissUntil.delete(key);
  }
  while (lookupMissUntil.size > LOOKUP_MISS_MAX) {
    const first = lookupMissUntil.keys().next().value;
    if (first == null) break;
    lookupMissUntil.delete(first);
  }
}

/** 记录一次 getById 404，短时内跳过重复请求 */
export function rememberImageSpaceLookupMiss(id) {
  const s = String(id || '').trim();
  if (!s) return;
  const now = Date.now();
  lookupMissUntil.set(s, now + LOOKUP_MISS_TTL_MS);
  pruneLookupMissCache(now);
}

/**
 * 是否应跳过 /admin/image-space/{id}：本地占位、落盘中、或近期已知不存在。
 */
export function shouldSkipImageSpaceServerLookup(id) {
  const s = String(id || '').trim();
  if (!s || isImageSpaceClientLocalId(s)) return true;
  if (pendingLookupIds.has(s)) return true;
  const until = lookupMissUntil.get(s);
  if (until == null) return false;
  if (Date.now() < until) return true;
  lookupMissUntil.delete(s);
  return false;
}

function fnv1aHash32(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * 生成图片空间 record_id（数据库上限 64 字符）。
 * 同一 namespace + sourceId 始终返回相同 id，重复导入时走 upsert。
 */
export function buildImageSpaceImportId(namespace, sourceId) {
  const ns = String(namespace || 'imp').replace(/[^a-zA-Z0-9-]/g, '').slice(0, 16) || 'imp';
  const src = String(sourceId || '').trim();
  if (!src) {
    return `LIB-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  }
  const direct = `${ns}-${src}`;
  if (direct.length <= MAX_RECORD_ID_LEN) {
    return direct;
  }
  const compact = `${ns}-${fnv1aHash32(`${ns}:${src}`)}`;
  return compact.length <= MAX_RECORD_ID_LEN ? compact : compact.slice(0, MAX_RECORD_ID_LEN);
}
